// src/Components/VideochatHost.jsx
//
// Montado una sola vez en el root de la app (ver App.jsx). Dueño de: el
// socket global de videochat, los listeners de handshake (request/accepted/
// rejected/cancelled/ended — ÚNICO lugar donde se escuchan, si quedan
// duplicados en ChatScreen el modal aparece dos veces y se rompe el guard
// anti doble-OFFER), el timeout de "ring", el auto-clear de "rechazado" y el
// montaje fullscreen de VideochatView. Así la solicitud entrante suena desde
// cualquier pantalla y la llamada activa sobrevive a la navegación.
import { useEffect, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import axios from 'axios'
import config from '../Config'
import { authUserAtom, dataUserAtom, hideHeaderAtom } from '../Services/store/atoms'
import {
  conversationsAtom, incomingVideochatAtom, activeVideochatAtom, outgoingVideochatAtom,
  startActiveVideochatAtom, cancelVideochatAtom, acceptVideochatAtom, rejectVideochatAtom, callDeclinedAtom,
  videochatMinimizedAtom,
} from '../Services/videochatAtoms'
import { connectVideochatSocket, disconnectVideochatSocket, getVideochatSocket } from '../Services/videochatSocket'
import VideochatView from './VideochatView'

const RING_TIMEOUT_MS = 45000
const CONVERSATIONS_REFRESH_MS = 60000

export default function VideochatHost() {
  const authUser = useAtomValue(authUserAtom)
  const token = authUser?.token
  const dataUser = useAtomValue(dataUserAtom)

  const [conversations, setConversations] = useAtom(conversationsAtom)
  const [incoming, setIncoming] = useAtom(incomingVideochatAtom)
  const [active, setActive] = useAtom(activeVideochatAtom)
  const [outgoing, setOutgoing] = useAtom(outgoingVideochatAtom)
  const startActive = useSetAtom(startActiveVideochatAtom)
  const cancelCall = useSetAtom(cancelVideochatAtom)
  const acceptCall = useSetAtom(acceptVideochatAtom)
  const rejectCall = useSetAtom(rejectVideochatAtom)
  const declineCall = useSetAtom(callDeclinedAtom)
  const [minimized, setMinimized] = useAtom(videochatMinimizedAtom)
  const setHideHeader = useSetAtom(hideHeaderAtom)

  // Los listeners se registran una sola vez por conexión → leer el estado
  // actual vía refs en vez de re-suscribirse en cada cambio (evita closures viejas).
  const incomingRef = useRef(incoming); incomingRef.current = incoming
  const activeRef   = useRef(active);   activeRef.current   = active
  const outgoingRef = useRef(outgoing); outgoingRef.current = outgoing
  // Misma fuente con la que requestVideochatAtom arma el `fromId` que viaja
  // en la solicitud, para poder reconocer las propias.
  const userIdRef   = useRef(null);     userIdRef.current   = dataUser?.id

  // ── Socket global: conectar con la sesión, desconectar en logout ──
  useEffect(() => {
    if (!token) {
      disconnectVideochatSocket()
      setIncoming(null)
      setOutgoing(null)
      setActive(null)
      return
    }
    connectVideochatSocket(token)
    return () => disconnectVideochatSocket()
  }, [token, setIncoming, setOutgoing, setActive])

  // ── Lista de conversaciones: qué rooms unir + nombre/avatar del otro
  //     usuario. Se refresca cada 1 min para que los chats nuevos (que
  //     ChatScreen todavía no vio) también sumen su room. ──
  useEffect(() => {
    if (!token) { setConversations([]); return }
    let cancelled = false
    const fetchConversations = async () => {
      try {
        axios.defaults.headers.common.Authorization = `Bearer ${token}`
        const res = await axios.get(`${config.REACT_APP_BASE_URL}/api/v1/chat/list`)
        const list = Array.isArray(res.data) ? res.data : res.data?.data
        if (!cancelled && Array.isArray(list)) setConversations(list)
      } catch (err) {
        console.warn('[VideochatHost] error al cargar conversaciones:', err)
      }
    }
    fetchConversations()
    const interval = setInterval(fetchConversations, CONVERSATIONS_REFRESH_MS)
    return () => { cancelled = true; clearInterval(interval) }
  }, [token, setConversations])

  // ── Membresía de rooms: TODAS las conversaciones, re-join en cada
  //     reconexión (el server pierde las rooms al desconectar; socket.io
  //     reconecta solo pero llega a rooms vacías). ──
  useEffect(() => {
    const socket = getVideochatSocket()
    if (!token || !socket || conversations.length === 0) return
    const joinAll = () => conversations.forEach(c => {
      if (c?.chat?.id != null) socket.emit('join', c.chat.id)
    })
    if (socket.connected) joinAll()
    socket.on('connect', joinAll)
    return () => socket.off('connect', joinAll)
  }, [token, conversations])

  // ── Listeners globales de videochat (único lugar donde se escuchan) ──
  useEffect(() => {
    const socket = getVideochatSocket()
    if (!token || !socket) return

    // Una llamada a la vez: ignorar mientras suena otra o hay una activa.
    const handleRequest = (data) => {
      if (incomingRef.current || activeRef.current) return
      // No mostrarse la propia llamada. El relay reparte a toda la room del
      // chat y solo excluye el SOCKET que emitió, no al usuario: con la misma
      // cuenta abierta en otro lado (la web mientras se llama desde la app, o
      // dos pestañas), esa otra sesión recibía la solicitud y le aparecía a
      // quien estaba llamando en vez de al destinatario. Es el mismo caso que
      // ya contemplaban los guards de handleAccepted/handleRejected, que se
      // apoyan en `outgoing`; acá no sirve, porque la sesión que muestra el
      // modal no es la que llamó y por lo tanto no tiene `outgoing`.
      if (data?.fromId != null && userIdRef.current != null
          && String(data.fromId) === String(userIdRef.current)) return
      setIncoming({
        chatId: String(data.chatId),
        fromName: data.fromName,
        fromId: data.fromId,
        requesterSocketId: data.requesterSocketId,
      })
    }
    const handleCancelled = () => setIncoming(null)
    // Guard anti SDP-glare: solo la instancia que REALMENTE envió la solicitud
    // arranca la llamada (misma cuenta abierta en dos pestañas también recibe
    // este evento de room; si ambas montaran la llamada, las dos enviarían
    // OFFER y quedaría colgada en "Conectando...").
    const handleAccepted = () => {
      if (outgoingRef.current?.status !== 'requesting') return
      const chatId = outgoingRef.current.chatId
      setOutgoing(null)
      startActive(chatId)
    }
    const handleRejected = () => {
      if (outgoingRef.current?.status !== 'requesting') return
      setOutgoing({ chatId: outgoingRef.current.chatId, status: 'rejected' })
    }
    const handleEnded = () => { setIncoming(null); setOutgoing(null); setActive(null) }
    // El relay no reenvió mi solicitud porque alguien está ocupado (el mismo
    // aviso llega también como 409 del HTTP: cuenta el primero).
    const handleBusy = ({ chatId, reason } = {}) => declineCall({ chatId, reason })

    socket.on('videochat-request',   handleRequest)
    socket.on('videochat-cancelled', handleCancelled)
    socket.on('videochat-accepted',  handleAccepted)
    socket.on('videochat-rejected',  handleRejected)
    socket.on('videochat-ended',     handleEnded)
    socket.on('videochat-busy',      handleBusy)
    return () => {
      socket.off('videochat-request',   handleRequest)
      socket.off('videochat-cancelled', handleCancelled)
      socket.off('videochat-accepted',  handleAccepted)
      socket.off('videochat-rejected',  handleRejected)
      socket.off('videochat-ended',     handleEnded)
      socket.off('videochat-busy',      handleBusy)
    }
  }, [token, setIncoming, setOutgoing, setActive, startActive, declineCall])

  // ── Ring timeout saliente: auto-cancelar a los 45s sin respuesta ──
  useEffect(() => {
    if (outgoing?.status !== 'requesting') return
    const t = setTimeout(() => cancelCall(), RING_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [outgoing, cancelCall])

  // ── 'rejected' es un banner transitorio: limpiarlo a los 3s ──
  useEffect(() => {
    if (outgoing?.status !== 'rejected') return
    const t = setTimeout(() => setOutgoing(null), 3000)
    return () => clearTimeout(t)
  }, [outgoing, setOutgoing])

  // ── Ocultar el header global mientras la llamada está en curso (el overlay
  //     fullscreen ya lo cubre todo, esto es solo para no dejarlo flotando
  //     por encima en algún layout con z-index más alto) ──
  useEffect(() => {
    setHideHeader(!!active && !minimized)
    return () => setHideHeader(false)
  }, [active, minimized, setHideHeader])

  // Una llamada nueva siempre arranca a pantalla completa.
  useEffect(() => { if (!active) setMinimized(false) }, [active, setMinimized])

  return (
    <>
      {/* Modal de solicitud entrante — flota sobre cualquier pantalla */}
      {incoming && (
        <div className="fixed inset-0 z-[10008] flex items-center justify-center bg-black/60 px-6">
          <div className="w-full max-w-xs rounded-2xl bg-gray-900 p-6 text-center shadow-xl">
            <p className="mb-2 text-base font-semibold text-white">📹 Solicitud de Videochat</p>
            <p className="mb-4 text-sm text-gray-400">
              <span className="font-medium text-white">{incoming.fromName || 'Usuario'}</span>{' '}
              quiere iniciar un videochat contigo
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => acceptCall()}
                className="flex-1 rounded-full bg-green-600 py-2 text-sm font-semibold text-white hover:bg-green-700"
              >
                Aceptar
              </button>
              <button
                onClick={() => rejectCall()}
                className="flex-1 rounded-full bg-red-600 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aviso transitorio: ocupado, bloqueo o rechazo con mensaje (3 s, ver arriba) */}
      {outgoing?.status === 'rejected' && outgoing.message && (
        <div
          role="status"
          className="fixed left-1/2 -translate-x-1/2 z-[10008] rounded-full bg-gray-900/95 px-4 py-2 text-sm font-semibold text-white shadow-xl"
          style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
        >
          {outgoing.message}
        </div>
      )}

      {/* Llamada activa — fullscreen global, sobrevive a la navegación */}
      {active && (
        <div className={`fixed inset-0 z-[10010] ${minimized ? 'pointer-events-none' : ''}`}>
          <VideochatView
            minimized={minimized}
            onMinimize={() => setMinimized(true)}
            onRestore={() => setMinimized(false)}
            partnerId={active.partnerId}
            relaySocket={getVideochatSocket()}
            chatId={active.chatId}
            isCaller={!!active?.isCaller}
            onHangUp={() => setActive(null)}
            authToken={token}
            partnerAvatarUrl={active.partnerAvatarUrl}
            partnerName={active.partnerName}
          />
        </div>
      )}
    </>
  )
}
